import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminActions, getAdminProfile, resetApiCaches, useGetMyProfileQuery, User } from '@/entities/Admin';
import {
  useGetNewReportsCountQuery,
  useGetNewStaffRequestsCountQuery,
  useGetNewStudentRequestsCountQuery,
} from '@/entities/Reports';
import { destroyChatSocketClient } from '@/features/Chat';
import { AppImage } from '@/shared/ui/AppImage';
import { Svg } from '@/shared/ui/Svg';
import ArrowIcon from '@/shared/assets/icons/complex/arrow.complex.svg';
import SettingsIcon from '@/shared/assets/icons/sidebar/settings.svg';
import ExitIcon from '@/shared/assets/icons/sidebar/exit.svg';
import UserIcon from '@/shared/assets/icons/user-check.svg';
import { routerPath } from '@/shared/config/router';
import { useAppDispatch, useAppSelector } from '@/shared/lib/hooks';
import { useAdminAccess } from '@/shared/lib/adminAccess';
import styles from './UserProfile.module.scss';

export const UserProfile = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { canAccessPage } = useAdminAccess();
  const canSeeRequestsReports = canAccessPage('requestsReports');

  const profile: User | null = useAppSelector(getAdminProfile);
  useGetMyProfileQuery(undefined, { skip: !!profile });

  const { data: newReportsCountResponse } = useGetNewReportsCountQuery(undefined, {
    skip: !canSeeRequestsReports,
  });
  const { data: newStudentRequestsCountResponse } = useGetNewStudentRequestsCountQuery(undefined, {
    skip: !canSeeRequestsReports,
  });
  const { data: newStaffRequestsCountResponse } = useGetNewStaffRequestsCountQuery(undefined, {
    skip: !canSeeRequestsReports,
  });

  const requestsReportsBadge =
    (newStudentRequestsCountResponse?.data.newStudentRequestsCount ?? 0) +
    (newStaffRequestsCountResponse?.data.newStaffRequestsCount ?? 0) +
    (newReportsCountResponse?.data.newReportsCount ?? 0);

  const fullName = profile ? `${profile.firstName} ${profile.lastName}` : 'User';
  const initials = `${profile?.firstName?.charAt(0) || ''}${profile?.lastName?.charAt(0) || ''}`.toUpperCase() || 'U';

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const logoutHandler = (redirectPath?: string) => {
    dispatch(adminActions.logout());
    resetApiCaches();
    destroyChatSocketClient();

    if (redirectPath) {
      navigate(redirectPath);
    }
  };

  return (
    <div className={styles.root} ref={menuRef}>
      <div className="flex items-center justify-center overflow-hidden w-10 h-10 min-w-10 min-h-10 rounded-full flex-shrink-0 bg-neutral-200">
        {profile?.avatar ? (
          <AppImage
            src={profile.avatar}
            alt="User avatar"
            width="100%"
            height="100%"
            className="object-cover"
          />
        ) : (
          <span className="text-sm font-semibold leading-none text-grey-secondary">{initials}</span>
        )}
      </div>
      <div className="flex flex-col">
        <span className={styles.name}>{fullName}</span>
        <span className="text-sm text-grey-secondary">{profile?.email || ''}</span>
      </div>
      <button className={styles.button} onClick={handleMenuToggle}>
        <Svg Icon={ArrowIcon} className={isMenuOpen ? styles.arrowRotated : ''} />
      </button>

      {isMenuOpen && (
        <div className={styles.dropdown}>
          {canSeeRequestsReports && (
            <>
              <Link
                className={`${styles.menuItem} ${styles.menuItemRequests}`}
                to={routerPath.requestsReports}
                onClick={() => setIsMenuOpen(false)}
              >
                <Svg Icon={UserIcon} />
                <span className={styles.menuItemLabel}>Requests & Reports</span>
                <span className={styles.menuBadge}>{requestsReportsBadge}</span>
              </Link>
              <div className={styles.divider} />
            </>
          )}
          <a className={styles.menuItem} href={routerPath.settings}>
            <Svg Icon={SettingsIcon} />
            <span>Settings</span>
          </a>
          <div className={styles.divider} />
          <div
            role="presentation"
            className={`${styles.menuItem} ${styles.logoutItem}`}
            onClick={() => logoutHandler(routerPath.login)}
          >
            <Svg Icon={ExitIcon} />
            <span>Log out</span>
          </div>
        </div>
      )}
    </div>
  );
};
